import { type CSSProperties, type KeyboardEvent, type PointerEvent, useCallback, useEffect, useRef, useState } from "react"
import { Link, useParams } from "react-router-dom"
import { Chat } from "../components/opencode/Chat"
import { ArtifactsBrowser } from "../components/opencode/ArtifactsBrowser"
import { ErrorBoundary } from "../components/opencode/ErrorBoundary"
import { FileBrowser } from "../components/opencode/FileBrowser"
import { Terminal } from "../components/opencode/Terminal"
import { Spinner, useLoad } from "../components/ui"
import { api } from "../lib/api"
import { ensureChatSession } from "../lib/chatSession"
import { OpenCodeApi } from "../lib/opencodeApi"
import type { Session } from "../lib/opencodeTypes"

type SessionStatus =
  | "creating"
  | "starting"
  | "running"
  | "idle"
  | "stopping"
  | "stopped"
  | "failed"

type WorkspaceSession = {
  id: string
  workspaceId: string
  name: string
  status: SessionStatus
  error?: string | null
  repoUrl: string | null
  startup: {
    status: "pending" | "running" | "succeeded" | "failed" | "interrupted"
    startedAt: string | null
    finishedAt: string | null
    exitCode: number | null
  } | null
}

type PanelTab = "context" | "terminal" | "files" | "artifacts"
type MobileView = "chat" | PanelTab

const PANEL_WIDTH_KEY = "moderniser_session_panel_width"
const MIN_PANEL_WIDTH = 320
const MAX_PANEL_WIDTH = 720

export function SessionTerminalPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const [apiInstance, setApiInstance] = useState<OpenCodeApi | null>(null)
  // One chat per session, resolved once the proxy client exists. There is no
  // list and no switching — the container is the unit, and the conversation
  // inside it is singular.
  const [chat, setChat] = useState<Session | null>(null)
  const [chatLoading, setChatLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [panelTab, setPanelTab] = useState<PanelTab>("context")
  const [mobileView, setMobileView] = useState<MobileView>("chat")
  const [contextContainer, setContextContainer] = useState<HTMLDivElement | null>(null)
  const [panelWidth, setPanelWidth] = useState(() => {
    const stored = typeof window === "undefined" ? NaN : Number(localStorage.getItem(PANEL_WIDTH_KEY))
    return Number.isFinite(stored) ? Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, stored)) : 420
  })
  const [displayName, setDisplayName] = useState("New session")
  const syncedTitle = useRef<string | null>(null)
  const [startupLog, setStartupLog] = useState<string | null>(null)
  const [startupLogError, setStartupLogError] = useState<string | null>(null)

  // Guards the create inside ensureChatSession against React StrictMode, which
  // double-invokes effects on mount: both runs would find no session and both
  // would create one. A ref survives the remount, which a piece of state used
  // the same way would not.
  const resolvingChat = useRef(false)

  const selectPanel = (tab: PanelTab) => {
    setPanelTab(tab)
    setMobileView(tab)
  }

  const session = useLoad(async () => {
    if (!sessionId) throw new Error("No session ID")
    console.log("[SessionTerminal] Loading session:", sessionId)
    const response = await api.get<{ session: WorkspaceSession }>(`/workspace-sessions/${sessionId}`)
    console.log("[SessionTerminal] Session loaded:", response.session)
    return response.session
  }, [sessionId])

  useEffect(() => {
    if (session.data?.name) setDisplayName(session.data.name)
  }, [session.data?.name])

  // Stable on purpose. Chat holds this in the dependency array of the effect
  // that opens the event stream and loads the history, so a new function every
  // render tore that subscription down and re-fetched the transcript — which
  // the message list shows as its loading skeleton. Depending on the name
  // rather than on `session.data` for the same reason as the effect below:
  // useLoad hands back a new object every render.
  const syncGeneratedTitle = useCallback(
    (title: string) => {
      const current = session.data?.name
      if (!sessionId || current !== "New session" || syncedTitle.current === title) return
      syncedTitle.current = title
      setDisplayName(title)
      void api
        .patch<{ session: WorkspaceSession }>(`/workspace-sessions/${sessionId}/title`, { title })
        .then((response) => setDisplayName(response.session.name))
        .catch(() => {
          syncedTitle.current = null
          setDisplayName(current ?? "New session")
        })
    },
    [sessionId, session.data?.name],
  )

  useEffect(() => {
    if (!session.data) return

    console.log("[SessionTerminal] Initializing session:", session.data.id, session.data.name)
    console.log("[SessionTerminal] Session status:", session.data.status)

    // The chat client exists only once the container answers — pointing it at
    // a session that is still cloning produced calls the proxy can only fail.
    if (session.data.status !== "running") return

    const instance = new OpenCodeApi(
      `${window.location.origin}/api/workspace-sessions/${sessionId}/proxy`,
      "proxy", // Username doesn't matter, proxy handles auth
      "proxy", // Password doesn't matter, proxy handles auth
      true, // Use proxy mode (cloud JWT instead of Basic auth)
    )
    setApiInstance(instance)
    // Keyed on the id, not the object. The visibility handler below reloads the
    // workspace session row on every window focus, and depending on `session.data`
    // meant a new identity each time — a fresh client, and Chat, Terminal and
    // Files all remounting because each takes `api` as a dependency.
  }, [session.data?.id, session.data?.status, sessionId])

  // Resolve the one chat. Separate from the effect above so it does not re-run
  // when the workspace session row is refreshed on tab focus.
  useEffect(() => {
    if (!apiInstance || resolvingChat.current) return
    resolvingChat.current = true

    ensureChatSession(apiInstance)
      .then((resolved) => {
        setChat(resolved)
        setError(null)
      })
      .catch((err) => {
        console.error("[SessionTerminal] Failed to open the chat:", err)
        setError(err instanceof Error ? err.message : "Failed to open the chat")
        // Released so a later reload can try again rather than being stuck.
        resolvingChat.current = false
      })
      .finally(() => setChatLoading(false))
  }, [apiInstance])

  // Clicking Start flips the screen to "Starting…" immediately — the status
  // in the database still says stopped (or failed) until the server has
  // spoken to the container host, and without this the button looks dead.
  const [kicking, setKicking] = useState(false)
  const [kickError, setKickError] = useState<string | null>(null)
  const kickStart = () => {
    setKicking(true)
    setKickError(null)
    void api
      .post(`/workspace-sessions/${sessionId}/start`)
      .then(() => session.reload())
      .catch((err) => {
        setKicking(false)
        setKickError(err instanceof Error ? err.message : "Could not start the session")
      })
  }
  // Once the row itself says something other than stopped/failed, the real
  // status drives the screen again.
  useEffect(() => {
    const status = session.data?.status
    if (kicking && status && status !== "stopped" && status !== "failed") setKicking(false)
  }, [kicking, session.data?.status])

  // While the session is being prepared (or wound down), watch it: the create
  // request returns immediately now, and this poll is what turns the
  // preparation screen into the chat the moment the container answers.
  // `kicking` polls too, in case the start was accepted but the row has not
  // flipped to `starting` yet.
  const transient =
    session.data?.status === "creating" ||
    session.data?.status === "starting" ||
    session.data?.status === "stopping"
  const setupActive = session.data?.startup?.status === "pending" || session.data?.startup?.status === "running"
  useEffect(() => {
    if (!transient && !kicking && !setupActive) return
    const timer = setInterval(() => session.reload(), 2500)
    return () => clearInterval(timer)
  }, [transient, kicking, setupActive, session])

  // An idle session auto-starts — one nudge, then the poll above takes over.
  const nudged = useRef(false)
  useEffect(() => {
    if (session.data?.status !== "idle" || nudged.current) return
    nudged.current = true
    void api
      .post(`/workspace-sessions/${sessionId}/start`)
      .then(() => session.reload())
      .catch(() => {
        nudged.current = false
      })
  }, [session.data?.status, session, sessionId])

  // Refresh session status when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("[SessionTerminal] Page became visible, refreshing session status")
        session.reload()
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange)
  }, [session])

  const maxPanelWidth = () => Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, window.innerWidth * 0.6))
  const clampPanelWidth = (value: number) => Math.round(Math.min(maxPanelWidth(), Math.max(MIN_PANEL_WIDTH, value)))

  useEffect(() => {
    localStorage.setItem(PANEL_WIDTH_KEY, String(panelWidth))
  }, [panelWidth])

  useEffect(() => {
    const clamp = () => setPanelWidth((width) => clampPanelWidth(width))
    window.addEventListener("resize", clamp)
    return () => window.removeEventListener("resize", clamp)
  }, [])

  const beginResize = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    const startX = event.clientX
    const startWidth = panelWidth
    const move = (next: globalThis.PointerEvent) => {
      setPanelWidth(clampPanelWidth(startWidth + startX - next.clientX))
    }
    const stop = () => {
      document.removeEventListener("pointermove", move)
      document.removeEventListener("pointerup", stop)
    }
    document.addEventListener("pointermove", move)
    document.addEventListener("pointerup", stop)
  }

  const resizeByKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    let next: number | undefined
    if (event.key === "ArrowLeft") next = panelWidth + 20
    if (event.key === "ArrowRight") next = panelWidth - 20
    if (event.key === "Home") next = MIN_PANEL_WIDTH
    if (event.key === "End") next = maxPanelWidth()
    if (next === undefined) return
    event.preventDefault()
    setPanelWidth(clampPanelWidth(next))
  }

  const showStartupLog = async () => {
    if (!sessionId) return
    setStartupLogError(null)
    try {
      const response = await api.get<{ log: string }>(`/workspace-sessions/${sessionId}/startup-log`)
      setStartupLog(response.log || "No output was captured.")
    } catch (cause) {
      setStartupLogError(cause instanceof Error ? cause.message : "Could not load setup output")
    }
  }

  // A background refresh must not unmount the page: the chat composer below
  // holds an unsent draft, and useLoad sets `loading` on every reload(), not
  // only the first. Only the initial load, with no row yet, has nothing to show.
  if (session.loading && !session.data) {
    return (
      <div className="flex h-full items-center justify-center bg-surface-muted">
        <Spinner />
      </div>
    )
  }

  // A refresh that fails while a row is already on screen is not worth throwing
  // the chat away for — a stale row renders fine and the next refresh fixes it.
  if (!session.data) {
    const errorMessage = session.error instanceof Error
      ? session.error.message
      : "Session not found"
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-surface-muted">
        <p className="text-sm text-danger">{errorMessage}</p>
        <Link to="/workspaces" className="text-sm text-accent hover:underline">
          ← Back to projects
        </Link>
      </div>
    )
  }

  const s = session.data
  const isRunning = s.status === "running"
  const isStarting = s.status === "starting" || s.status === "creating"
  const isIdle = s.status === "idle"

  // The preparation screen. Everything before "running" lands here, with words
  // matched to what is actually happening — the clone is the slow part, and a
  // spinner with no explanation reads as a hang.
  if (isStarting || isIdle || s.status === "stopping" || kicking) {
    const line =
      s.status === "creating"
        ? "Preparing the container — cloning the repository and starting the agent. Usually under a minute; a large repository can take a few."
        : s.status === "stopping"
          ? "Stopping the session…"
          : "Starting the session…"
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-surface">
        <Spinner />
        <p className="text-lg font-semibold text-foreground">{s.name}</p>
        <p className="max-w-md text-center text-sm text-muted">{line}</p>
        <Link to="/sessions" className="text-xs text-faint hover:text-foreground">
          ← Back to sessions — it keeps preparing without you
        </Link>
      </div>
    )
  }

  if (s.status === "failed") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-surface px-6">
        <p className="text-lg font-semibold text-foreground">{s.name}</p>
        <p className="text-sm text-danger">The session could not start.</p>
        {s.error && (
          <pre className="max-w-xl overflow-x-auto rounded-md border border-border bg-surface-muted p-3 text-xs text-muted">{s.error}</pre>
        )}
        {kickError && <p className="max-w-md text-center text-sm text-danger">{kickError}</p>}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={kickStart}
            className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-surface-muted"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirm("Delete this failed session?")) return
              void api.delete(`/workspace-sessions/${s.id}`).then(() => (window.location.href = "/sessions"))
            }}
            className="rounded-md border border-border px-3 py-1.5 text-sm text-danger hover:bg-danger-soft"
          >
            Delete session
          </button>
        </div>
        <Link to="/sessions" className="text-xs text-faint hover:text-foreground">
          ← Back to sessions
        </Link>
      </div>
    )
  }

  if (!isRunning) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 bg-surface">
        <p className="text-lg font-semibold text-foreground">{s.name}</p>
        <p className="text-sm text-muted">Session is {s.status}</p>
        {kickError && <p className="max-w-md text-center text-sm text-danger">{kickError}</p>}
        <button
          type="button"
          onClick={kickStart}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-foreground hover:bg-surface-muted"
        >
          Start session
        </button>
        <Link to="/sessions" className="text-sm text-accent hover:underline">
          ← Back to sessions
        </Link>
      </div>
    )
  }

  const mobileViews: Array<{ id: MobileView; label: string }> = [
    { id: "chat", label: "Chat" },
    { id: "context", label: "Context" },
    { id: "terminal", label: "Terminal" },
    { id: "files", label: "Files" },
    { id: "artifacts", label: "Artifacts" },
  ]
  const panelViews: Array<{ id: PanelTab; label: string }> = [
    { id: "context", label: "Context" },
    { id: "terminal", label: "Terminal" },
    { id: "files", label: "Files" },
    { id: "artifacts", label: "Artifacts" },
  ]
  const setup = s.startup
  const setupTone = setup?.status === "failed" || setup?.status === "interrupted" ? "text-danger" : "text-muted"

  return (
    <ErrorBoundary>
      <div className="flex h-full flex-col bg-surface">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center border-b border-border/60 px-3 py-1.5">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to="/workspaces"
              title="Back to workspaces"
              className="rounded-md px-1.5 py-0.5 text-sm text-muted transition-colors hover:bg-surface-muted hover:text-foreground"
            >
              ←
            </Link>
            <h1 className="truncate text-sm font-medium text-foreground">{displayName}</h1>
          </div>

          <div className="flex items-center justify-center gap-0.5 lg:hidden">
            {mobileViews.map((view) => (
              <button
                key={view.id}
                type="button"
                onClick={() => {
                  setMobileView(view.id)
                  if (view.id !== "chat") setPanelTab(view.id)
                }}
                className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                  mobileView === view.id
                    ? "bg-surface-muted text-foreground"
                    : "text-muted hover:text-foreground"
                }`}
              >
                {view.label}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-end gap-2.5">
            <span title={s.status} className="inline-flex items-center">
              {isStarting && <span className="text-xs text-warning">Starting…</span>}
              {isIdle && <span className="text-xs text-muted">Idle</span>}
              {isRunning && <span aria-label="Running" className="size-2 rounded-full bg-success" />}
            </span>
          </div>
        </div>

        {error && (
          <div className="border-b border-border bg-danger-soft px-4 py-2">
            <p className="text-sm text-danger">{error}</p>
          </div>
        )}

        {setup && (
          <div className="border-b border-border bg-surface-muted/50 px-4 py-1.5 text-xs">
            <div className="flex items-center gap-2">
              {(setup.status === "pending" || setup.status === "running") && (
                <span className="size-2 animate-pulse rounded-full bg-warning" aria-hidden />
              )}
              <span className={setupTone}>
                {setup.status === "pending" && "Startup setup is waiting to run."}
                {setup.status === "running" && "Startup setup is running in the background; Chat and Terminal are ready."}
                {setup.status === "succeeded" && "Startup setup completed."}
                {setup.status === "failed" && `Startup setup failed${setup.exitCode == null ? "" : ` (exit ${setup.exitCode})`}.`}
                {setup.status === "interrupted" && "Startup setup was interrupted and will not run again."}
              </span>
              <button type="button" onClick={() => void showStartupLog()} className="text-accent hover:underline">
                View output
              </button>
            </div>
            {startupLogError && <p className="mt-1 text-danger">{startupLogError}</p>}
            {startupLog !== null && (
              <pre className="mt-2 max-h-40 overflow-auto rounded border border-border bg-surface p-2 font-mono text-[11px] text-muted">{startupLog}</pre>
            )}
          </div>
        )}

        <div className="flex flex-1 overflow-hidden">
          <div className={`${mobileView === "chat" ? "flex" : "hidden"} min-w-0 flex-1 flex-col overflow-hidden lg:flex`}>
            {chat && apiInstance ? (
              <Chat
                api={apiInstance}
                session={chat}
                workspaceSessionId={session.data.id}
                contextContainer={contextContainer}
                contextSelected={panelTab === "context"}
                onShowContext={() => selectPanel("context")}
                onTitleChange={syncGeneratedTitle}
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-muted">
                {chatLoading ? "Opening chat..." : "Chat unavailable"}
              </div>
            )}
          </div>

          <div
            role="separator"
            aria-label="Resize workspace panel"
            aria-orientation="vertical"
            aria-valuemin={MIN_PANEL_WIDTH}
            aria-valuemax={Math.round(maxPanelWidth())}
            aria-valuenow={panelWidth}
            tabIndex={0}
            onPointerDown={beginResize}
            onKeyDown={resizeByKeyboard}
            className="group hidden w-1 shrink-0 cursor-col-resize bg-border outline-none hover:bg-accent focus:bg-accent lg:block"
            title="Drag to resize. Arrow keys resize by 20 pixels."
          />

          <aside
            className={`${mobileView === "chat" ? "hidden" : "flex"} min-w-0 w-full flex-1 flex-col overflow-hidden bg-surface lg:flex lg:w-[var(--session-panel-width)] lg:max-w-[60vw] lg:flex-none`}
            style={{ "--session-panel-width": `${panelWidth}px` } as CSSProperties}
          >
            <div className="flex shrink-0 items-center gap-0.5 border-b border-border px-2 py-1.5">
              {panelViews.map((view) => (
                <button
                  key={view.id}
                  type="button"
                  onClick={() => selectPanel(view.id)}
                  className={`rounded-md px-2.5 py-1 text-xs transition-colors ${
                    panelTab === view.id ? "bg-surface-muted text-foreground" : "text-muted hover:text-foreground"
                  }`}
                >
                  {view.label}
                </button>
              ))}
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <div ref={setContextContainer} className={panelTab === "context" ? "h-full" : "hidden"} />
              <div className={panelTab === "terminal" ? "h-full" : "hidden"}>
                {apiInstance && <Terminal api={apiInstance} sessionId={sessionId!} />}
              </div>
              <div className={panelTab === "files" ? "h-full" : "hidden"}>
                {apiInstance && <FileBrowser api={apiInstance} />}
              </div>
              <div className={panelTab === "artifacts" ? "h-full" : "hidden"}>
                {apiInstance && <ArtifactsBrowser api={apiInstance} />}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </ErrorBoundary>
  )
}
